import React from "react"
import { Card } from "react-bootstrap"
import { Trans, useTranslation } from "react-i18next"
import { HiOutlineEye, HiOutlineEyeOff } from "react-icons/hi"
import { twClassNames } from "../../../../helpers/classNames"
import { GuideImage, GuideVideoThumbnail } from "."
import { GuideTextPreview } from "./text"
import type { ContentBlock, Guide, GuideImageData, VideoData } from "../../../../constants/types"

type GuidePreviewProps = {
  guide: Guide | undefined
  blocks: ContentBlock[]
  language: string
  onVideoClick: (data: Record<string, unknown>) => void
}

export const GuidePreview: React.FC<GuidePreviewProps> = ({ guide, blocks, language, onVideoClick }) => {
  const { t } = useTranslation()

  return (
    <Card className="mb-4">
      <Card.Header className="tw-flex tw-justify-between">
        <h5 className="mb-0">{t("guides.preview")}</h5>
        {guide && (
          <div className="tw-group tw-relative">
            <span
              className={twClassNames(
                "tw-mb-0 tw-place-self-center tw-text-sm",
                guide.isDraft ? "tw-text-red-400" : "tw-text-dark-50"
              )}>
              {guide.isDraft ? (
                <>
                  <HiOutlineEyeOff className="tw-mr-1" />
                  {t("guides.form.draft")}
                </>
              ) : (
                <>
                  <HiOutlineEye className="tw-mr-1" />
                  {t("guides.form.publish")}
                </>
              )}
            </span>
            <span
              className="tw-absolute -tw-right-3 tw-top-8 tw-z-50 tw-hidden tw-w-[300px] tw-rounded-md tw-border
                tw-border-dark-15 tw-bg-white tw-p-3 tw-text-left tw-text-sm tw-opacity-0 tw-shadow-md
                tw-transition-opacity tw-duration-200 tw-ease-in group-hover:tw-block group-hover:tw-opacity-100">
              {guide.isDraft ? (
                <Trans i18nKey="guides.tooltips.isDraft.draft" />
              ) : (
                <Trans i18nKey="guides.tooltips.isDraft.publish" />
              )}
            </span>
          </div>
        )}
      </Card.Header>
      <div className="mb-3">
        {blocks.map(({ data, type }, index) => {
          switch (type) {
            case "header":
            case "paragraph":
              return <GuideTextPreview key={index} data={data as never} language={language} type={type} />
            case "image":
              return <GuideImage key={index} data={data as GuideImageData} list={false} preview full={false} />
            case "video":
              return (
                <GuideVideoThumbnail
                  key={index}
                  data={data as VideoData}
                  preview
                  list
                  onClick={onVideoClick}
                />
              )
            default:
              return null
          }
        })}
        {!guide && <p className="text-muted font-italic m-0 px-4 pt-3">{t("guides.selectedGuide.unselected")}</p>}
        {guide && !blocks.length && (
          <p className="text-muted font-italic m-0 px-4 pt-3">{t("guides.selectedGuide.noContent")}</p>
        )}
      </div>
    </Card>
  )
}
